import React, { useEffect, useState } from 'react'
import { useCookies } from 'react-cookie'
import { useRouter } from 'next/router'

import { VOTE_USER_INPUT } from 'types/comments'
import { ROLES } from 'types/user'
import { emptyTextArea } from 'types/editor'
import { Comment as CommentType } from 'services/apiTypes/comment.types'

import CommentService from 'services/comment.service'
import ToastService from 'services/toast.service'
import UserService from 'services/user.service'

import Link from 'components/elements/Link'
import Icon from 'components/elements/Icon'
import AvatarCircle from 'components/user/AvatarCircle'
import Editor from 'components/formElements/richText/Editor'
import CommentReplyForm from 'components/comments/ReplyForm'
import ArchidektDropdown from 'components/elements/ArchidektDropdown'
import CommentFlair from 'components/comments/Flair'
import { ShrinkButton } from 'components/formElements/Button'

import { timeSince } from 'utils/time.utils'
import { encodeUsernameRoute } from 'utils/encoding'

import styles from './comment.module.scss'

type Props = {
  comment: CommentType
  onCommentChange: (updatedComment: CommentType) => void
  onCollapse?: () => void
  style?: React.CSSProperties
  newsPostStyling?: boolean
  className?: string
  archived?: boolean
  locked?: boolean
}

const DELETED_OWNER_ID = -1 // Used as a placeholder for deleted comments, used to hide controls of deleted comments

const Comment = ({
  comment,
  style = {},
  className = '',
  onCommentChange,
  onCollapse,
  archived,
  locked,
  newsPostStyling = false,
}: Props) => {
  const [{ tbId: userId, tbR: roles = [] }] = useCookies(['tbId', 'tbR'])

  const router = useRouter()

  const [editing, setEditing] = useState(false)
  const [editedState, setEditedState] = useState<string>(comment.text)
  const [replying, setReplying] = useState(false)

  // When a new comment is added to a comment chain, it pushes the comment down in the component tree
  // When this happens, the comment itself changes, but since the same component (eg: a comment) is replacing it
  // react doesn't reinitalize the component from scratch
  // Since we're using our own state to track the edited comment we need to update that state when the comment changes
  useEffect(() => setEditedState(comment.text), [comment])

  const handleArchive = () => {
    CommentService.update({ ...comment, archived: true })
      .then(onCommentChange)
      .then(() => ToastService.create('Thread has been archived', 'Comment Service', 'success'))
      .catch(() => ToastService.create('Unable to archive comment at this time'))
  }

  const handleLock = () => {
    CommentService.update({ ...comment, locked: true })
      .then(onCommentChange)
      .then(() => ToastService.create('Thread has been locked', 'Comment Service', 'success'))
      .catch(() => ToastService.create('Unable to lock comment at this time'))
  }

  const handleDelete = () => {
    if (comment.children.length) return ToastService.create('Unable to delete comment with children')

    const stubbedDeletedData: Partial<CommentType> = {
      owner: { id: DELETED_OWNER_ID, username: 'removed', avatar: '' },
      text: emptyTextArea,
    }

    CommentService.hardDelete(comment)
      .then(() => onCommentChange({ ...comment, ...stubbedDeletedData }))
      .catch(() => ToastService.create('Unable to delete comment'))
  }

  const handleSaveEdit = () => {
    return CommentService.update({ ...comment, text: editedState })
      .then(updatedComment => {
        setEditing(false)
        onCommentChange(updatedComment)
      })
      .catch(() => ToastService.create('Unable to update comment at this time'))
  }

  const handleVote = (upvote: boolean) => {
    // Determins whether or not we're removing or adding the comment based on the current comments userInput
    const remove =
      comment.userInput !== VOTE_USER_INPUT.NONE &&
      ((comment.userInput === VOTE_USER_INPUT.UP && upvote) || (comment.userInput === VOTE_USER_INPUT.DOWN && !upvote))

    // Builds the new user's vote from the request data
    const userInput = remove ? VOTE_USER_INPUT.NONE : upvote ? VOTE_USER_INPUT.UP : VOTE_USER_INPUT.DOWN

    // Builds the new point totally locally
    // I'm not convinced this should even be here, we could just not recalculate points, but for now, this is fine
    let points = comment.points

    if (comment.userInput === VOTE_USER_INPUT.NONE && upvote) points += 1
    if (comment.userInput === VOTE_USER_INPUT.NONE && !upvote) points -= 1
    if (comment.userInput === VOTE_USER_INPUT.UP && upvote) points -= 1
    if (comment.userInput === VOTE_USER_INPUT.DOWN && upvote) points += 2
    if (comment.userInput === VOTE_USER_INPUT.UP && !upvote) points -= 2
    if (comment.userInput === VOTE_USER_INPUT.DOWN && !upvote) points += 1

    CommentService.vote(comment, { remove, up: upvote })
      .then(() => onCommentChange({ ...comment, userInput, points }))
      .catch(() => ToastService.create('Oops! Something went wrong voting. Unable to vote right now'))
  }

  const handleShareComment = () => {
    const shareUrl = `${window.location.origin}/forum/thread/${comment.id}`

    const shareData = {
      title: comment.title || 'Check out this comment on Archidekt!',
      text: comment.text,
      url: shareUrl,
    }

    if (navigator.share && navigator.canShare(shareData)) return navigator.share(shareData)

    navigator.clipboard.writeText(shareUrl)

    ToastService.create('Comment link copied!', '', 'success')
  }

  const handleOpenRoot = () => {
    // Navigating this way rather than a link since the dropdown is a list of buttons
    const win = window.open(`/forum/thread/${comment.originalPost}`, '_blank')

    win?.focus()
  }

  const handleLockUser = () => {
    // prettier-ignore
    const message = `Are you sure you want to lock user '${comment.owner.username}'? Doing so will prevent them from doing anything on the site until unlocked, but will not delete any of their existing data.`

    if (!window.confirm(message)) return

    UserService.lock(comment.owner.id)
      .then(() => ToastService.create('User has been locked', 'User Service', 'success'))
      .catch(() => ToastService.create('Unable to lock user at this time', 'User Service'))
  }

  const handlePromptLogin = () => {
    router.push('/login')
  }

  const handleWeirdMinimize = (e: React.MouseEvent) => {
    if (!onCollapse) return
    if (newsPostStyling) return

    onCollapse()
  }

  const isMod = roles.includes(ROLES.MOD)
  const canEdit = (!archived && isMod) || `${comment.owner.id}` === userId
  const canArchive = !archived && isMod
  const disableInteractions = archived || locked

  const upvoted = comment.userInput === VOTE_USER_INPUT.UP
  const downvoted = comment.userInput === VOTE_USER_INPUT.DOWN

  return (
    <div className={`${styles.container} ${newsPostStyling ? styles.newsPost : ''} ${className}`} style={style}>
      <div className={styles.avatar} onClick={handleWeirdMinimize}>
        <AvatarCircle src={comment.owner.avatar} />
      </div>

      <div className={styles.content}>
        <div>
          <div className={styles.metaInfo}>
            <Link href={`/u/${encodeUsernameRoute(comment.owner.username)}`} className={styles.username}>
              {/* The icon and img are toggled between with css */}
              <Icon name="user" className={styles.userIcon} />
              <img
                className={styles.smallUserIcon}
                src={comment.owner.avatar}
                alt={`${comment.owner.username} avatar`}
              />
              {comment.owner.username}
              <CommentFlair comment={comment} />
            </Link>{' '}
            {timeSince(comment.createdAt)}
          </div>
        </div>

        {/* Using separate editor components here to keep onChange from removing edited state if you toggle between editing and not editing */}
        {!editing && <Editor value={comment.text} readOnly />}
        {editing && (
          <>
            <Editor
              autofocus
              value={editedState}
              onChange={setEditedState}
              onSubmit={handleSaveEdit}
              onCancel={() => setEditing(false)}
            />
          </>
        )}
        {comment.editedAt && <div className={styles.edited}>Edited {new Date(comment.editedAt).toLocaleString()} </div>}
        {comment.owner.id !== DELETED_OWNER_ID && (
          <div className={styles.controls}>
            {/* Interactions */}
            <div className={styles.gap}>
              <ShrinkButton
                onClick={() => (userId ? setReplying(true) : handlePromptLogin())}
                icon="reply"
                disabled={disableInteractions}>
                Reply
              </ShrinkButton>
              <ShrinkButton
                disabled={disableInteractions}
                onClick={() => (userId ? handleVote(true) : handlePromptLogin())}
                icon="thumbs up"
                className={upvoted ? styles.upvote : ''}
              />
              <span>{comment.points}</span>
              <ShrinkButton
                disabled={disableInteractions}
                onClick={() => (userId ? handleVote(false) : handlePromptLogin())}
                icon="thumbs down"
                className={downvoted ? styles.downvote : ''}
              />
            </div>

            <ArchidektDropdown
              options={[
                { label: 'Share', icon: 'share alternate', onClick: handleShareComment },
                { label: 'Root Post', icon: 'level up alternate', onClick: handleOpenRoot, hidden: !comment.parent },
                { label: 'Archive', icon: 'archive', onClick: handleArchive, hidden: !canArchive },
                { label: 'Lock thread', icon: 'lock', onClick: handleLock, hidden: !isMod },
                { label: 'Lock user', icon: 'gavel', onClick: handleLockUser, hidden: !isMod },
                { label: 'Edit', icon: 'edit', onClick: () => setEditing(!editing), hidden: !canEdit },
                { label: 'Delete', icon: 'trash alternate', onClick: handleDelete, hidden: !canEdit },
              ]}
            />
          </div>
        )}

        <CommentReplyForm
          show={replying}
          parentComment={comment}
          onClose={() => setReplying(false)}
          onReplySubmitted={onCommentChange}
        />
      </div>
    </div>
  )
}

export default Comment
